import type {
  RefreshControlProps,
  SectionListData,
  SectionListProps,
} from 'react-native'
import React from 'react'
import { RefreshControl, SectionList } from 'react-native'

import { useWebPullToRefresh } from '../../hooks/useWebPullToRefresh'
import { WebPullToRefreshIndicator } from './WebPullToRefreshIndicator'

type PullToRefreshSectionListProps<
  ItemT,
  SectionT extends SectionListData<ItemT>,
> = Omit<SectionListProps<ItemT, SectionT>, 'refreshControl'> & {
  refreshing: boolean
  onRefresh: () => Promise<void> | void
  indicatorColor?: string
  refreshControlProps?: Omit<RefreshControlProps, 'refreshing' | 'onRefresh'>
}

type ResolvedListHeaderComponent = PullToRefreshSectionListProps<
  unknown,
  SectionListData<unknown>
>['ListHeaderComponent']

function RenderListHeader({
  listHeaderComponent,
}: {
  listHeaderComponent: PullToRefreshSectionListProps<
    unknown,
    SectionListData<unknown>
  >['ListHeaderComponent']
}) {
  if (listHeaderComponent == null) {
    return null
  }

  if (React.isValidElement(listHeaderComponent)) {
    return listHeaderComponent
  }

  return React.createElement(listHeaderComponent)
}

type PullToRefreshSectionListHeaderProps = {
  color?: string
  enabled: boolean
  listHeaderComponent: ResolvedListHeaderComponent
  pullDistance: number
  refreshing: boolean
  shouldRelease: boolean
}

function PullToRefreshSectionListHeader({
  color,
  enabled,
  listHeaderComponent,
  pullDistance,
  refreshing,
  shouldRelease,
}: PullToRefreshSectionListHeaderProps) {
  return (
    <>
      <WebPullToRefreshIndicator
        enabled={enabled}
        pullDistance={pullDistance}
        refreshing={refreshing}
        shouldRelease={shouldRelease}
        color={color}
      />
      <RenderListHeader listHeaderComponent={listHeaderComponent} />
    </>
  )
}

export function PullToRefreshSectionList<
  ItemT,
  SectionT extends SectionListData<ItemT>,
>({
  refreshing,
  onRefresh,
  indicatorColor,
  refreshControlProps,
  onScroll,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  ListHeaderComponent,
  ...sectionListProps
}: PullToRefreshSectionListProps<ItemT, SectionT>) {
  const webPullToRefresh = useWebPullToRefresh({
    onRefresh,
    refreshing,
  })

  return (
    <SectionList
      {...sectionListProps}
      onScroll={(event) => {
        webPullToRefresh.handleScroll(event)
        onScroll?.(event)
      }}
      onTouchStart={(event) => {
        webPullToRefresh.handleTouchStart(event)
        onTouchStart?.(event)
      }}
      onTouchMove={(event) => {
        webPullToRefresh.handleTouchMove(event)
        onTouchMove?.(event)
      }}
      onTouchEnd={(event) => {
        webPullToRefresh.handleTouchEnd(event)
        onTouchEnd?.(event)
      }}
      onPointerDown={(event) => {
        webPullToRefresh.handlePointerDown(event)
        onPointerDown?.(event)
      }}
      onPointerMove={(event) => {
        webPullToRefresh.handlePointerMove(event)
        onPointerMove?.(event)
      }}
      onPointerUp={(event) => {
        webPullToRefresh.handlePointerUp(event)
        onPointerUp?.(event)
      }}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          {...refreshControlProps}
        />
      }
      ListHeaderComponent={
        <PullToRefreshSectionListHeader
          color={indicatorColor}
          enabled={webPullToRefresh.isEnabled}
          listHeaderComponent={
            ListHeaderComponent as ResolvedListHeaderComponent
          }
          pullDistance={webPullToRefresh.pullDistance}
          refreshing={refreshing}
          shouldRelease={webPullToRefresh.shouldRelease}
        />
      }
    />
  )
}
